import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import './Suscripcion.css'

export default function Suscripcion({ toast, comercioId }) {
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState(null)
  const [dataComercio, setDataComercio] = useState({
    mensajes_disponibles: 0,
    creditos_demo: 50,
    plan_actual: 'trial',
    estado_suscripcion: 'trial',
    permitir_audios: false,
    plan_vence_el: null 
  })

  const infoPlanes = {
    trial: { max: 50, nombre: 'Prueba Gratuita' },
    basico: { max: 1000, nombre: 'Básico' },
    pro: { max: 3500, nombre: 'Pro' },
    premium: { max: 10000, nombre: 'Premium' },
    vip: { max: 10000, nombre: 'VIP' } 
  }

  useEffect(() => {
    const cargarDatos = async () => {
      if (!comercioId) return
      try {
        const { data, error } = await supabase
          .from('comercios')
          .select('mensajes_disponibles, creditos_demo, plan_actual, estado_suscripcion, permitir_audios, plan_vence_el')
          .eq('id', comercioId)
          .single()

        if (error) throw error
        
        if (data) {
          // 🧼 Normalizamos pasando a minúsculas y eliminando tildes
          const planNormalizado = data.plan_actual 
            ? data.plan_actual.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
            : 'trial'
          
          setDataComercio({
            mensajes_disponibles: data.mensajes_disponibles || 0,
            creditos_demo: data.creditos_demo ?? 50,
            plan_actual: planNormalizado,
            estado_suscripcion: data.estado_suscripcion || 'trial',
            permitir_audios: data.permitir_audios || false,
            plan_vence_el: data.plan_vence_el
          })
        }
      } catch (err) {
        console.error("Error cargando suscripción:", err)
        if (typeof toast === 'function') toast('Error al cargar datos', 'error')
      } finally {
        setLoading(false)
      }
    }
    cargarDatos()
  }, [comercioId, toast])

  const isActiva = dataComercio.estado_suscripcion === 'activa'

  // 🔒 SEGURIDAD: Si la suscripción no está activa, se fuerza visualmente el plan 'trial'
  const planSeguro = isActiva && infoPlanes[dataComercio.plan_actual] 
    ? dataComercio.plan_actual 
    : 'trial'

  const maxCreditos = infoPlanes[planSeguro].max
  
  // ⚖️ BALANCE DE CRÉDITOS: Idéntico al Sidebar
  const creditosActuales = isActiva 
    ? dataComercio.mensajes_disponibles 
    : dataComercio.creditos_demo

  const porcentaje = Math.max(0, Math.min(100, (creditosActuales / maxCreditos) * 100))

  const isPremiumFeatureUnlocked = ['pro', 'premium', 'vip'].includes(planSeguro)

  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return null
    const opciones = { day: 'numeric', month: 'short', year: 'numeric' }
    const fecha = new Date(fechaISO)
    return fecha.toLocaleDateString('es-ES', opciones)
  }

  const toggleAudios = async () => {
    if (!isPremiumFeatureUnlocked) {
      if (typeof toast === 'function') toast('Necesitás el plan PRO para activar audios', 'error')
      return
    }

    const nuevoEstado = !dataComercio.permitir_audios
    setDataComercio(prev => ({ ...prev, permitir_audios: nuevoEstado }))
    
    try {
      const { error } = await supabase
        .from('comercios')
        .update({ permitir_audios: nuevoEstado })
        .eq('id', comercioId)
        
      if (error) throw error
      if (typeof toast === 'function') toast(nuevoEstado ? 'Audios activados' : 'Audios desactivados', 'success')
    } catch (error) {
      setDataComercio(prev => ({ ...prev, permitir_audios: !nuevoEstado }))
      if (typeof toast === 'function') toast('Error al actualizar', 'error')
    }
  }

  const handleCheckout = async (tipoPlan) => {
    setCheckoutLoading(tipoPlan)
    try {
      // 🌟 URL dinámica basada en variables de entorno (Vite)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://istore-ai-agent-production.up.railway.app'
      
      const response = await fetch(`${apiUrl}/api/checkout/crear-preferencia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comercio_id: comercioId, tipo_plan: tipoPlan })
      })

      const data = await response.json()
      if (data.init_point) {
        window.location.href = data.init_point
      } else {
        throw new Error("No link")
      }
    } catch (error) {
      if (typeof toast === 'function') toast("Error al conectar con MercadoPago", "error")
      setCheckoutLoading(null) 
    }
  }

  const renderBoton = (planId, textoNormal) => {
    if (checkoutLoading === planId) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
            <line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
          Procesando...
        </span>
      )
    }
    return planSeguro === planId ? 'Tu Plan Actual' : textoNormal
  }

  if (loading) {
    return (
      <div className="sus-loading">
        <svg className="sus-spinner" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle></svg>
        <p>Cargando información...</p>
      </div>
    )
  }

  return (
    <div className="sus-container">
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>

      <header className="sus-header">
        <h1 className="sus-title">Mi Suscripción</h1>
        <p className="sus-subtitle">Gestioná tus créditos y los superpoderes de tu bot.</p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '3rem' }}>
        
        {/* 🚨 BANNER DE ALERTA DE SERVICIO PAUSADO (SALDO 0) */}
        {creditosActuales <= 0 && (
          <div style={{ 
            backgroundColor: '#fef2f2', 
            color: '#991b1b', 
            padding: '1.25rem', 
            borderRadius: '12px', 
            border: '2px solid #f87171', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px',
            boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.1)'
          }}>
            <span style={{ fontSize: '2rem' }}>🛑</span>
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: 'bold' }}>¡Servicio Pausado!</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.4' }}>
                Tu bot se ha quedado sin mensajes y <strong>ha dejado de responder</strong> a tus clientes. 
                Por favor, seleccioná un plan debajo para reactivarlo automáticamente.
              </p>
            </div>
          </div>
        )}

        {/* SECCIÓN HERO: ESTADO ACTUAL */}
        <section className="sus-card sus-card-consumo" style={{ width: '100%', margin: 0 }}>
          <div className="sus-card-header" style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem' }}>Consumo del mes</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: 'normal' }}>Estado general de tu cuenta y mensajes restantes</p>
            </div>
            <span className={`badge ${isActiva ? 'badge-active' : 'badge-trial'}`} style={{ padding: '6px 12px' }}>
              {isActiva ? `Plan ${infoPlanes[planSeguro].nombre.toUpperCase()}` : 'PRUEBA GRATUITA'}
            </span>
          </div>
          
          <div className="consumo-info" style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span className="consumo-numero" style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0f172a' }}>{creditosActuales}</span>
            <span className="consumo-texto" style={{ color: '#64748b', fontWeight: '500' }}>/ {maxCreditos} mensajes disponibles</span>
          </div>
          
          <div className="sus-progress-bg" style={{ marginTop: '1rem', height: '10px', borderRadius: '10px' }}>
            <div 
              className="sus-progress-fill" 
              style={{ width: `${porcentaje}%`, backgroundColor: porcentaje < 15 ? '#ef4444' : '#10b981', borderRadius: '10px' }}
            />
          </div>
          
          {isActiva && dataComercio.plan_vence_el && (
             <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '12px', textAlign: 'right', fontWeight: '500' }}>
               Ciclo renueva el: {formatearFecha(dataComercio.plan_vence_el)}
             </p>
          )}
        </section>

        {/* SECCIÓN FUNCIONES */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
          
          {/* Tarjeta Audios */}
          <section className={`sus-card ${!isPremiumFeatureUnlocked ? 'card-locked' : ''}`} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'flex-start' }}>
            <div className="sus-card-header" style={{ marginBottom: 0 }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>Transcripción de Audios</h3>
              {!isPremiumFeatureUnlocked && <span className="badge badge-locked">Solo PRO</span>}
            </div>
            
            <div className="toggle-row" style={{ paddingTop: '0.25rem' }}>
              <div className="toggle-info">
                <div className="info-title-row" style={{ marginBottom: '4px' }}>
                  <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Permitir notas de voz</h4>
                  <span className="badge-cost" style={{ fontSize: '0.75rem' }}> 3 msjs / audio</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>El bot escuchará y responderá los audios de tus clientes.</p>
              </div>
              
              <label className={`toggle-switch ${!isPremiumFeatureUnlocked ? 'disabled' : ''}`}>
                <input 
                  type="checkbox" 
                  checked={dataComercio.permitir_audios && isPremiumFeatureUnlocked}
                  onChange={toggleAudios}
                  disabled={!isPremiumFeatureUnlocked}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </section>

          {/* Tarjeta Post-Venta */}
          <section className={`sus-card ${!isPremiumFeatureUnlocked ? 'card-locked' : ''}`} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'flex-start' }}>
            <div className="sus-card-header" style={{ marginBottom: 0 }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>Post-Venta Automático</h3>
              {!isPremiumFeatureUnlocked ? (
                <span className="badge badge-locked">Solo PRO</span>
              ) : (
                <span className="badge badge-active" style={{ background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}>Activo</span>
              )}
            </div>
            
            <div className="toggle-row" style={{ paddingTop: '0.25rem', alignItems: 'center' }}>
              <div className="toggle-info">
                <div className="info-title-row" style={{ marginBottom: '4px' }}>
                  <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Fidelización Inteligente</h4>
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>Mensajes automáticos de satisfacción y upselling tras cada venta.</p>
              </div>
              <div style={{ fontSize: '1.5rem', opacity: isPremiumFeatureUnlocked ? 1 : 0.4, paddingLeft: '10px' }}>
                {isPremiumFeatureUnlocked ? '🚀' : '🔒'}
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* SECCIÓN PLANES */}
      <div className="planes-header">
        <h2 className="planes-title">Potenciá tus ventas automáticas</h2>
      </div>
      
      <div className="planes-wrapper">
        {/* Básico */}
        <div className={`pricing-card ${planSeguro === 'basico' ? 'card-current' : ''}`}>
          <div className="pricing-header">
            <h3 className="plan-name">Básico</h3>
            <div className="plan-price">
              <span className="currency">$</span><span className="amount">15.000</span><span className="period">/ mes</span>
            </div>
          </div>
          <ul className="plan-features">
            <li><span className="check">✓</span> <strong>1.000 mensajes</strong></li>
            <li><span className="check">✓</span> Filtro Anti-Spam</li>
            <li className="disabled"><span className="cross">✕</span> Reconocimiento de audios</li>
            <li className="disabled"><span className="cross">✕</span> Mensajes Post-venta</li>
          </ul>
          <button 
            className={`btn-plan ${planSeguro === 'basico' ? 'btn-disabled' : 'btn-outline'}`} 
            onClick={() => handleCheckout('basico')} 
            disabled={checkoutLoading !== null || planSeguro === 'basico'}
          >
            {renderBoton('basico', 'Seleccionar Básico')}
          </button>
        </div>

        {/* Pro */}
        <div className={`pricing-card card-popular ${planSeguro === 'pro' ? 'card-current' : ''}`}>
          <div className="popular-ribbon">Más Elegido</div>
          <div className="pricing-header">
            <h3 className="plan-name text-gradient">PRO</h3>
            <div className="plan-price">
              <span className="currency">$</span><span className="amount">35.000</span><span className="period">/ mes</span>
            </div>
          </div>
          <ul className="plan-features">
            <li><span className="check pro-check">✓</span> <strong>3.500 mensajes</strong></li>
            <li><span className="check pro-check">✓</span> Filtro Anti-Spam</li>
            <li><span className="check pro-check">✓</span> Reconocimiento de audios</li>
            <li><span className="check pro-check">✓</span> Mensajes Post-venta</li>
          </ul>
          <button 
            className={`btn-plan ${planSeguro === 'pro' ? 'btn-disabled' : 'btn-gradient'}`} 
            onClick={() => handleCheckout('pro')} 
            disabled={checkoutLoading !== null || planSeguro === 'pro'}
          >
            {renderBoton('pro', 'Obtener Plan PRO')}
          </button>
        </div>

        {/* Premium */}
        <div className={`pricing-card ${['premium', 'vip'].includes(planSeguro) ? 'card-current' : ''}`}>
          <div className="pricing-header">
            <h3 className="plan-name">Premium</h3>
            <div className="plan-price">
              <span className="currency">$</span><span className="amount">85.000</span><span className="period">/ mes</span>
            </div>
          </div>
          <ul className="plan-features">
            <li><span className="check">✓</span> <strong>10.000 mensajes</strong></li>
            <li><span className="check">✓</span> Filtro Anti-Spam</li>
            <li><span className="check">✓</span> Reconocimiento de audios</li>
            <li><span className="check">✓</span> Mensajes Post-venta</li>
          </ul>
          <button 
            className={`btn-plan ${['premium', 'vip'].includes(planSeguro) ? 'btn-disabled' : 'btn-outline'}`} 
            onClick={() => handleCheckout('premium')} 
            disabled={checkoutLoading !== null || ['premium', 'vip'].includes(planSeguro)}
          >
            {renderBoton('premium', 'Seleccionar Premium')}
          </button>
        </div>
      </div>
    </div>
  )
}